// A group of settings drawn from the API's registry (GET …/settings): one field per setting, typed by the registry,
// showing where the value in force comes from (this partner, its client, the platform, the default) and any change
// waiting for a second system administrator. Adding a setting to the API's registry adds its field here.
// `sections` groups the fields under headings that open and close (the few that matter first, the rest folded); a
// field no section names still shows, in a last "Other" section.
import { useState, type FormEvent } from 'react';
import { type SettingRow, type SettingValue } from '../api';

const SOURCE_LABEL: Record<SettingRow['source'], string> = {
  program: 'Set here',
  parent: 'From its partner',
  platform: 'Platform default',
  default: 'Built-in default',
};

/** What the field shows: the scope's own value, or blank (inherits) when it has none. */
function initial(row: SettingRow): string | boolean {
  const v = row.hasOwn ? row.own : null;
  if (row.type === 'bool') return row.hasOwn ? v === true : row.value === true;
  if (row.type === 'ipList' || row.type === 'textList') return Array.isArray(v) ? v.join('\n') : '';
  return v === null || v === undefined ? '' : String(v);
}

const shown = (row: SettingRow, v: SettingValue) =>
  v === null
    ? 'None'
    : Array.isArray(v)
      ? v.length === 0
        ? row.type === 'ipList'
          ? 'Any address'
          : 'None'
        : v.join(', ')
      : row.type === 'bool'
        ? v
          ? 'Yes'
          : 'No'
        : row.type === 'percent'
          ? `${String(v)}%`
          : String(v);

/** The empty field: what it inherits (unknown here once the scope has its own value). */
const inheritLabel = (row: SettingRow) => (row.hasOwn ? 'Empty: inherit' : `Inherited: ${shown(row, row.value)}`);

export interface SettingsSection {
  title: string;
  /** One line under the heading: what these settings are for. */
  note?: string;
  keys: readonly string[];
  /** Open at first (the others open on a click, or by themselves when one of their fields waits or is refused). */
  open?: boolean;
}

export function SettingsForm({
  rows,
  editable,
  busy,
  onSave,
  sections,
}: {
  rows: SettingRow[];
  editable: boolean;
  busy: boolean;
  /** Returns each field's problem, if the API refused some. */
  onSave: (change: { set: Record<string, SettingValue>; clear: string[] }) => Promise<Record<string, string>>;
  sections?: readonly SettingsSection[];
}) {
  const fields = rows.filter((r) => !r.managed);
  const [form, setForm] = useState<Record<string, string | boolean>>(() =>
    Object.fromEntries(fields.map((r) => [r.key, initial(r)])),
  );
  const [problems, setProblems] = useState<Record<string, string>>({});
  const groups: { title: string; note?: string; rows: SettingRow[]; open: boolean }[] = sections
    ? [
        ...sections.map((sec) => ({
          title: sec.title,
          note: sec.note,
          rows: sec.keys.map((k) => fields.find((f) => f.key === k)).filter((r): r is SettingRow => !!r),
          open: sec.open === true,
        })),
        {
          title: 'Other',
          rows: fields.filter((f) => !sections.some((sec) => sec.keys.includes(f.key))),
          open: true,
        },
      ].filter((g) => g.rows.length > 0)
    : [{ title: '', rows: fields, open: true }];
  const [opened, setOpened] = useState<Set<string>>(
    () => new Set(groups.filter((g) => g.open || g.rows.some((r) => r.pending)).map((g) => g.title)),
  );

  async function save(e: FormEvent) {
    e.preventDefault();
    const set: Record<string, SettingValue> = {};
    const clear: string[] = [];
    for (const row of fields) {
      const before = initial(row);
      const now = form[row.key]!;
      if (now === before) continue;
      if (row.type === 'bool') set[row.key] = now === true;
      else if (row.type === 'ipList' || row.type === 'textList') {
        const list = String(now)
          // addresses: any space or comma; words: one per line (a word may hold spaces)
          .split(row.type === 'ipList' ? /[\s,]+/ : /\n+/)
          .map((v) => v.trim())
          .filter(Boolean);
        if (list.length === 0) clear.push(row.key);
        else set[row.key] = list;
      } else if (now === '') {
        if (row.hasOwn) clear.push(row.key);
      } else if (row.type === 'int') set[row.key] = Number(now);
      else set[row.key] = String(now).trim();
    }
    const refused = await onSave({ set, clear });
    setProblems(refused);
    const withProblems = groups.filter((g) => g.rows.some((r) => refused[r.key])).map((g) => g.title);
    if (withProblems.length) setOpened(new Set([...opened, ...withProblems]));
  }

  async function inherit(row: SettingRow) {
    setProblems(await onSave({ set: {}, clear: [row.key] }));
  }

  function grid(groupRows: SettingRow[]) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        {groupRows.map((row) => (
          // a label over its control, its help under it (a bare label in the grid ran them all on one line)
          <label key={row.key} className="qc-account-field">
            <span>
              {row.label}
              {row.needsApproval && (
                <span className="qc-badge qc-badge-purple" style={{ marginLeft: 6 }}>
                  Needs approval
                </span>
              )}
            </span>
            {row.type === 'bool' ? (
              <input
                type="checkbox"
                name={row.key}
                style={{ width: 18, height: 18 }}
                disabled={!editable}
                checked={form[row.key] === true}
                onChange={(e) => setForm({ ...form, [row.key]: e.target.checked })}
              />
            ) : row.type === 'ipList' || row.type === 'textList' ? (
              <textarea
                name={row.key}
                rows={4}
                disabled={!editable}
                placeholder={
                  row.type === 'ipList' ? 'One per line, e.g.\n203.0.113.10\n198.51.100.0/24' : inheritLabel(row)
                }
                value={String(form[row.key])}
                onChange={(e) => setForm({ ...form, [row.key]: e.target.value })}
                style={{ fontFamily: 'monospace' }}
              />
            ) : row.type === 'enum' ? (
              <select
                name={row.key}
                disabled={!editable}
                value={String(form[row.key])}
                onChange={(e) => setForm({ ...form, [row.key]: e.target.value })}
              >
                <option value="">{inheritLabel(row)}</option>
                {row.options?.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                inputMode={row.type === 'int' ? 'numeric' : 'decimal'}
                name={row.key}
                disabled={!editable}
                placeholder={inheritLabel(row)}
                value={String(form[row.key])}
                onChange={(e) => setForm({ ...form, [row.key]: e.target.value })}
              />
            )}
            <small className="qc-field-hint">
              {row.help} <strong>In force: {shown(row, row.value)}</strong> · {SOURCE_LABEL[row.source]}
              {row.hasOwn && editable && (
                <>
                  {' · '}
                  <button
                    type="button"
                    className="qc-link-button"
                    disabled={busy}
                    onClick={() => void inherit(row)}
                    style={{ background: 'none', border: 0, padding: 0, color: 'var(--qor-accent)', cursor: 'pointer' }}
                  >
                    Use inherited
                  </button>
                </>
              )}
            </small>
            {row.pending && (
              <small className="qc-field-hint" style={{ color: 'var(--qor-warning, #b26b00)' }}>
                Waiting for approval: {row.pending.clear ? 'back to inherited' : shown(row, row.pending.value)} (asked
                by {row.pending.requestedBy})
              </small>
            )}
            {problems[row.key] && (
              <small className="qc-result-text" role="alert">
                {problems[row.key]}
              </small>
            )}
          </label>
        ))}
      </div>
    );
  }

  return (
    <form onSubmit={(e) => void save(e)} className="qc-account-field">
      {groups.map((g) =>
        g.title === '' ? (
          <div key="all">{grid(g.rows)}</div>
        ) : (
          <section key={g.title} className="qc-settings-section">
            <button
              type="button"
              className="qc-settings-section-head"
              aria-expanded={opened.has(g.title)}
              onClick={() => {
                const next = new Set(opened);
                if (!next.delete(g.title)) next.add(g.title);
                setOpened(next);
              }}
            >
              <span aria-hidden="true">{opened.has(g.title) ? '▾' : '▸'}</span> {g.title}
              <small>
                {g.rows.length} setting{g.rows.length === 1 ? '' : 's'}
                {g.rows.some((r) => r.hasOwn) && ' · some set here'}
              </small>
            </button>
            {g.note && <p className="qc-field-hint">{g.note}</p>}
            {opened.has(g.title) && grid(g.rows)}
          </section>
        ),
      )}
      {editable ? (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 6 }}>
          <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
            Save settings
          </button>
          <span style={{ color: 'var(--qor-muted)', fontSize: '.8rem' }}>
            Empty a field to inherit. Changes marked &ldquo;Needs approval&rdquo; wait for a second system
            administrator.
          </span>
        </div>
      ) : (
        <p style={{ color: 'var(--qor-muted)', fontSize: '.8rem', margin: 0 }}>
          Only QOR&apos;s system administrators change these settings.
        </p>
      )}
    </form>
  );
}
